<template>
  <div data-component="app-launcher-versions">
    <div class="title">
      {{ $t('HITL.app-launcher.versions-title') }}
    </div>

    <div class="versions-row">
      <or-select
        data-control="app-versions"
        :model-value="sActive.appVersion"
        :options="searchOptions"
        :placeholder="$t('HITL.app-launcher.versions-placeholder')"
        :disabled="isDisabled"
        enable-search
        @search-change="search = $event"
        @update:modelValue="onVersionChange"
      >
        <template #optionTemplate="{ option }">
          <app-launcher-versions-option
            :version="option.label"
            :is-initial="isInitialVersion(option)"
            :needs-data-update="needsDataUpdate(option)"
          />
        </template>
      </or-select>

      <or-button
        v-if="showVersionSaveButton"
        data-button="save-version"
        :loading="isSaving"
        @click="$emit('save')"
      >
        {{ $t('HITL.common.actions.save') }}
      </or-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import { OrSelectV3 as OrSelect, OrButtonV3 as OrButton } from '@onereach/ui-components-25';

import AppLauncherVersionsOption from './AppLauncherVersionsOption.vue';
import { orderVersionsBySemVer, needsDataUpdate } from '../../common';
import {
  APP_VERSIONS_PROP,
  APP_SETTINGS_PROP,
  MIGRATIONS_BY_RANGE_PROP,
} from '../../constants';

export default defineComponent({
  name: 'AppLauncherVersions',
  components: {
    OrSelect,
    OrButton,
    AppLauncherVersionsOption,
  },
  props: {
    sActive: APP_SETTINGS_PROP,
    sInitial: APP_SETTINGS_PROP,
    versions: APP_VERSIONS_PROP,
    migrationsByRange: MIGRATIONS_BY_RANGE_PROP,
    isDisabled: Boolean,
    isSaving: Boolean,
  },
  emits: ['settings-change', 'save'],
  data() {
    return {
      search: '',
    };
  },
  computed: {
    /** Show Save when another version is selected and it does not require data migration */
    showVersionSaveButton(): boolean {
      const vActive = this.sInitial.appVersion;
      const vTarget = this.sActive.appVersion;
      if (!vTarget || vTarget === vActive) {
        return false;
      }
      return !needsDataUpdate({
        vActive,
        vTarget,
        migrationsByRange: this.migrationsByRange,
      });
    },
    versionsOrderBySemVer() {
      return orderVersionsBySemVer(this.versions);
    },

    searchOptions() {
      const options = [];
      for (const item of this.versionsOrderBySemVer) {
        const isActiveVersion = item.version === this.sInitial.appVersion;
        const isRCVersion = !!item.prerelease.length;

        if (!isActiveVersion && isRCVersion && !this.sActive.showRCVersions) {
          continue;
        }

        const searchTerm = this.search.trim().toLowerCase();
        if (item.version.toLowerCase().includes(searchTerm)) {
          options.push({ label: item.version, value: item.version });
        }
      }

      return options;
    },
  },
  methods: {
    isInitialVersion(option: { value: string; }) {
      return option.value === this.sInitial.appVersion;
    },

    needsDataUpdate(option: { value: string; }) {
      return needsDataUpdate({
        vTarget: option.value,
        vActive: this.sInitial.appVersion,
        migrationsByRange: this.migrationsByRange,
      });
    },

    /** TODO: OrSelect shouldn't allow deselecting an option */
    onVersionChange(option: string) {
      if (option) {
        const settings = { ...this.sActive, appVersion: option };
        this.$emit('settings-change', settings);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='app-launcher-versions'] {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);

  .title {
    color: var(--or-c-on-background);
    font-weight: var(--fw-1);
  }

  .versions-row {
    display: flex;
    align-items: center;
    gap: var(--s-3);

    [data-control='app-versions'] {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
